import { useState, useRef, useEffect, useCallback } from 'react';

// Every server ack settles, so a press can never hang on "Opening mic"
const ACK_TIMEOUT_MS = 10000;

export function usePushToTalk({ socket, viewerId, selectedUnit, isDeviceReady, device, sendTransport }) {
  const [isPushToTalkActive, setIsPushToTalkActive] = useState(false);
  // Pressed, but the mic and producer are not live yet
  const [isPushToTalkConnecting, setIsPushToTalkConnecting] = useState(false);
  const localStream = useRef(null);
  const producerRef = useRef(null);
  const pttKeyRef = useRef(false);
  // Refs, not state: a release that lands while the mic is still opening must reach the start in flight
  const wantTalkRef = useRef(false);
  const startingRef = useRef(false);

  const releaseLocalStream = useCallback(() => {
    if (localStream.current) {
      localStream.current.getTracks().forEach(track => track.stop());
      localStream.current = null;
    }
  }, []);

  const stopPushToTalk = useCallback(() => {
    wantTalkRef.current = false;
    // A start in flight sees the release and cleans up after itself
    if (startingRef.current) return;

    try {
      if (producerRef.current) {
        console.log('🔇 Stopping Push-to-Talk...');
        producerRef.current.close();
        producerRef.current = null;
        console.log('✅ Push-to-Talk stopped');
      }
      releaseLocalStream();
    } catch (error) {
      console.error('❌ Error stopping Push-to-Talk:', error);
    }
    setIsPushToTalkActive(false);
  }, [releaseLocalStream]);

  const createSendTransport = useCallback(async () => {
    if (sendTransport.current) return sendTransport.current;
    if (!socket.current?.connected) {
      throw new Error('Viewer not connected to signaling server');
    }
    if (!isDeviceReady() || !device.current) {
      throw new Error('Device not ready yet');
    }

    return new Promise((resolve, reject) => {
      socket.current.timeout(ACK_TIMEOUT_MS).emit('createTransport', { direction: 'send' }, async (err, params) => {
        if (err || !params || params.error) {
          reject(new Error(params?.error || 'Server did not answer createTransport'));
          return;
        }

        try {
          const transport = device.current.createSendTransport(params);

          transport.on('connect', async ({ dtlsParameters }, callback, errback) => {
            try {
              socket.current.timeout(ACK_TIMEOUT_MS).emit('connectTransport', {
                direction: 'send',
                dtlsParameters
              }, (err, result) => {
                if (err || !result || result.error) errback(new Error(result?.error || 'Server did not answer connectTransport'));
                else callback();
              });
            } catch (error) {
              errback(error);
            }
          });

          transport.on('connectionstatechange', (state) => {
            console.log(`🔌 Send transport state: ${state}`);
            if (state === 'closed' || state === 'failed' || state === 'disconnected') {
              stopPushToTalk();
              sendTransport.current = null;
            }
          });

          transport.on('produce', async ({ kind, rtpParameters, appData }, callback, errback) => {
            try {
              console.log(`📤 Viewer producing audio to unit: ${appData?.targetUnitId}`);
              socket.current.timeout(ACK_TIMEOUT_MS).emit('produce', {
                kind,
                rtpParameters,
                metadata: {
                  viewerId: viewerId.current,
                  timestamp: Date.now(),
                  targetUnitId: appData?.targetUnitId,
                  isViewerAudio: true,
                }
              }, (err, result) => {
                if (err || !result || result.error) {
                  const message = result?.error || 'Server did not answer produce';
                  console.error(`❌ Error producing audio:`, message);
                  errback(new Error(message));
                } else {
                  console.log(`✅ Audio producer created: ${result.id} for unit ${appData?.targetUnitId}`);
                  callback({ id: result.id });
                }
              });
            } catch (error) {
              console.error(`❌ Exception in produce:`, error);
              errback(error);
            }
          });

          sendTransport.current = transport;
          resolve(transport);
        } catch (error) {
          reject(error);
        }
      });
    });
  }, [socket, viewerId, isDeviceReady, device, sendTransport, stopPushToTalk]);

  const startPushToTalk = useCallback(async () => {
    wantTalkRef.current = true;
    // Touch fires both touch and mouse events: one start at a time
    if (startingRef.current || producerRef.current) return;

    if (!selectedUnit) {
      alert('Select a unit first before speaking');
      return;
    }
    if (!socket.current?.connected) {
      alert('Not connected to the server yet');
      return;
    }
    if (!isDeviceReady() || !device.current) {
      alert('Initializing media device, please wait a moment...');
      return;
    }

    startingRef.current = true;
    setIsPushToTalkConnecting(true);
    try {
      console.log(`🎤 Starting Push-to-Talk to unit: ${selectedUnit}`);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        }
      });

      localStream.current = stream;
      const transport = await createSendTransport();
      const audioTrack = stream.getAudioTracks()[0];

      const producer = await transport.produce({
        track: audioTrack,
        codecOptions: {
          opusStereo: false,
          opusDtx: true,
          opusFec: true,
        },
        appData: {
          targetUnitId: selectedUnit,
          viewerId: viewerId.current,
          isViewerAudio: true,
        }
      });

      console.log(`🎤 Producer ID: ${producer.id}, Kind: ${producer.kind}`);

      if (!wantTalkRef.current) {
        // Released before the mic went live: never leave it open
        console.log('🔇 Push-to-Talk released while connecting');
        producer.close();
        releaseLocalStream();
        return;
      }

      producerRef.current = producer;
      setIsPushToTalkActive(true);
      console.log(`✅ Push-to-Talk active - Sending to ${selectedUnit}`);
    } catch (error) {
      console.error('❌ Error starting Push-to-Talk:', error);
      releaseLocalStream();
      alert('Cannot access microphone: ' + error.message);
    } finally {
      startingRef.current = false;
      setIsPushToTalkConnecting(false);
    }
  }, [selectedUnit, socket, isDeviceReady, device, viewerId, createSendTransport, releaseLocalStream]);

  // Keyboard shortcuts (Space key)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space' && !pttKeyRef.current && selectedUnit) {
        e.preventDefault();
        pttKeyRef.current = true;
        startPushToTalk();
      }
    };

    const handleKeyUp = (e) => {
      if (e.code === 'Space' && pttKeyRef.current) {
        e.preventDefault();
        pttKeyRef.current = false;
        stopPushToTalk();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [selectedUnit, startPushToTalk, stopPushToTalk]);

  const cleanup = useCallback(() => {
    wantTalkRef.current = false;
    if (producerRef.current) {
      producerRef.current.close();
      producerRef.current = null;
    }
    releaseLocalStream();
    if (sendTransport.current) {
      sendTransport.current.close();
      sendTransport.current = null;
    }
  }, [sendTransport, releaseLocalStream]);

  return {
    isPushToTalkActive,
    isPushToTalkConnecting,
    startPushToTalk,
    stopPushToTalk,
    cleanup,
  };
}
